import React, {useEffect, useState} from 'react';

import {
    SubmittedRequestTypeMapWithDefault, SubmittedStatusMap, SubmittedStatusMapWithDefault,
} from "./SubmittedRequestDomain";

import {SubmittedRequestListAction, SubmittedRequestListFilterValue} from "./submittedRequestListReducer";
import DatePicker from "react-datepicker";
import {DatePickerInput} from "../../../common/component/DatePickerInput";
import {AutoCompleteMultiInput, DropdownMultiSelect, MultiFilterButton, SimpleDropdown, showToast} from "common";
import {tryFormatLocalDate} from "../../../common/util/date";
import {SPLIT_DELIMITER, splitAndRemoveEmpty} from "../../../placement/const";

export type SubmittedRequestListFilterParams = {
    requestType: string | null;
    statuses: Array<string> | null;
    submittedBy: Array<string> | null;
    submittedDateFrom: string | null;
    submittedDateTo: string | null;
    closedDateFrom: string | null;
    closedDateTo: string | null;
}

export function asQueryParam(filter: SubmittedRequestListFilterValue | undefined): SubmittedRequestListFilterParams {
    return {
        statuses: filter?.status
            ? (filter.status.split(';').map(s => s.trim()).filter(Boolean))
            : null,
        submittedDateFrom: tryFormatLocalDate(filter?.submittedDateFrom ?? null),
        submittedDateTo: tryFormatLocalDate(filter?.submittedDateTo ?? null),
        closedDateFrom: tryFormatLocalDate(filter?.closedDateFrom ?? null),
        closedDateTo: tryFormatLocalDate(filter?.closedDateTo ?? null),
        requestType: filter?.requestType === '' ? null : filter?.requestType ?? null,
        submittedBy: filter?.submittedBy ? splitAndRemoveEmpty(filter.submittedBy) : null,
    };
}

export type SubmittedRequestListPreserveFields = Array<keyof SubmittedRequestListFilterValue>;

function buildResetFilterValue(
    filterValue: SubmittedRequestListFilterValue,
    preserveContextFields: SubmittedRequestListPreserveFields,
): SubmittedRequestListFilterValue {
    const preserve = new Set(preserveContextFields);
    return {
        status: null,
        submittedDateFrom: null,
        submittedDateTo: null,
        closedDateFrom: null,
        closedDateTo: null,
        requestType: null,
        submittedBy: null,
        ...(preserve.has('clientGuid') ? {clientGuid: filterValue.clientGuid || null} : {})
    };
}

type Props = {
    initFilterValue: SubmittedRequestListFilterValue,
    dispatch: React.Dispatch<SubmittedRequestListAction>,
    preserveContextFields?: SubmittedRequestListPreserveFields,
}

const CLOSED_RELATED_STATUSES = ['Closed', 'Resolved', 'Inactive', 'Decision Made', 'Completed'];

function validateFilter(val: SubmittedRequestListFilterValue): string | null {
    const subFrom = val.submittedDateFrom?.getTime();
    const subTo = val.submittedDateTo?.getTime();
    if (subFrom && subTo && subTo < subFrom) {
        return 'The "Submitted Date To" cannot be before the "Submitted From Date".';
    }
    const closedFrom = val.closedDateFrom?.getTime();
    const closedTo = val.closedDateTo?.getTime();
    if (closedFrom && closedTo && closedTo < closedFrom) {
        return 'The "Closed Date To" cannot be before the "Closed From Date".';
    }
    const hasClosedDates = !!(closedFrom || closedTo);
    const statuses = val.status ? val.status.split(';').map(s => s.trim()).filter(Boolean) : [];
    const hasClosedStatus = statuses.some(s => CLOSED_RELATED_STATUSES.includes(s));
    if (hasClosedDates && !hasClosedStatus) {
        return 'Closed Date is not applicable to the selected Status.';
    }
    return null;
}

export function SubmittedRequestListFilter({initFilterValue, dispatch, preserveContextFields = []}: Props) {
    const [filterValue, setFilterValue] = useState({...initFilterValue});
    // update internal value when init filter value updated
    useEffect(() => {
        setFilterValue(initFilterValue);
    }, [initFilterValue])

    const onFilterUpdate = (fieldName: string, fieldValue: any) => {
        setFilterValue({...filterValue, [fieldName]: fieldValue})
    }

    const dispatchSetFilterValue = (val: SubmittedRequestListFilterValue): boolean | void => {
        const error = validateFilter(val);
        if (error) {
            showToast.warning(error);
            return false;
        }
        dispatch({type: "SET_FILTER", filter: val});
    };

    return <MultiFilterButton<SubmittedRequestListFilterValue>
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        dispatchSetFilterValue={dispatchSetFilterValue}
        filterOptions={
            [
            {
                label: "Request Type",
                component: <SimpleDropdown
                    options={SubmittedRequestTypeMapWithDefault}
                    selected={filterValue.requestType || ''}
                    minWidth={"7rem"}
                    textColor={"gray-600"}
                    setSelected={(val) => onFilterUpdate("requestType", val)}/>
            },
            {
                label: "Submitted Date From",
                component: <DatePicker
                    wrapperClassName="w-full"
                    placeholderText="mm/dd/yyyy"
                    selected={filterValue.submittedDateFrom}
                    onSelect={(date: Date) => onFilterUpdate("submittedDateFrom", date)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="submittedDateFrom"
                        value={undefined}
                        onFinish={(date) => onFilterUpdate("submittedDateFrom", date)}
                    />}
                />
            },
            {
                label: "Submitted Date To",
                component: <DatePicker
                    wrapperClassName="w-full"
                    placeholderText="mm/dd/yyyy"
                    selected={filterValue.submittedDateTo}
                    onSelect={(newValue: Date | null) => onFilterUpdate("submittedDateTo", newValue)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="submittedDateTo"
                        value={undefined}
                        onFinish={(date) => onFilterUpdate("submittedDateTo", date)}
                    />}
                />
            },
            {
                label: "Submitted By",
                component: <AutoCompleteMultiInput
                    field="submittedBy"
                    fieldValue={filterValue.submittedBy || ''}
                    setFieldValue={(val) => onFilterUpdate("submittedBy", splitAndRemoveEmpty(val).join(SPLIT_DELIMITER) || null)}
                    queryType="submitter"
                    inputType="char-only"
                    limitLength={100}
                />
            },
            {
                label: "Status",
                component: <DropdownMultiSelect
                    field='status'
                    options={Array.from(SubmittedStatusMap.keys())}
                    labelMapperFunc={(val) => SubmittedStatusMap.get(val)!}
                    full
                    value={filterValue.status || ''}
                    setValue={(values) => onFilterUpdate("status", values)}/>

            },
            {
                label: "Closed Date From",
                component: <DatePicker
                    wrapperClassName="w-full"
                    placeholderText="mm/dd/yyyy"
                    selected={filterValue.closedDateFrom}
                    onSelect={(date: Date) => onFilterUpdate("closedDateFrom", date)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="closedDateFrom"
                        value={undefined}
                        onFinish={(date) => onFilterUpdate("closedDateFrom", date)}
                    />}
                />
            },
            {
                label: "Closed Date To",
                component: <DatePicker
                    wrapperClassName="w-full"
                    placeholderText="mm/dd/yyyy"
                    selected={filterValue.closedDateTo}
                    onSelect={(newValue: Date | null) => onFilterUpdate("closedDateTo", newValue)}
                    onChange={() => {}}
                    customInput={<DatePickerInput
                        fieldName="closedDateTo"
                        value={undefined}
                        onFinish={(date) => onFilterUpdate("closedDateTo", date)}
                    />}
                />
            },
        ]
        }
        emptyFilterValue={buildResetFilterValue(filterValue, preserveContextFields)}
    />;
}
